import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Phone,
  Monitor,
  LayoutGrid,
  MailPlus,
  Server,
  Users,
  ShieldCheck,
  Database,
  Cloud,
  Network,
  Lightbulb,
} from "lucide-react";
import { services, companyInfo } from "../data/mock";
import { useScrollReveal } from "../hooks/useScrollReveal";

const iconMap = {
  Monitor, LayoutGrid, MailPlus, Server, Users, ShieldCheck,
  DatabaseBackup: Database, Cloud, Network, Lightbulb,
};

const Section = ({ children, className = "" }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section
      ref={ref}
      className={`reveal ${isVisible ? "visible" : ""} ${className}`}
    >
      {children}
    </section>
  );
};

export default function ServiceDetail() {
  const { serviceId } = useParams();
  const service = services.find((s) => s.id === serviceId);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const Icon = iconMap[service.icon] || Monitor;
  const currentIndex = services.findIndex((s) => s.id === serviceId);
  const relatedServices = services
    .filter((s) => s.id !== serviceId)
    .slice(0, 3);

  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="container mx-auto px-6 page-enter">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
            <Link to="/services" className="hover:text-blue-600 transition-colors">
              Services
            </Link>
            <span>/</span>
            <span className="text-slate-900 font-medium">{service.title}</span>
          </div>
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
              <Icon className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight mb-3">
                {service.seoTitle || service.title}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
                {service.description}
              </p>
            </div>
          </div>

          {/* Quick trust strip */}
          <div className="flex flex-wrap gap-4 mt-8 pt-6 border-t border-slate-200">
            {[
              "20+ years experience",
              "Local Australian team",
              "No lock-in contracts",
            ].map((item, i) => (
              <span key={i} className="flex items-center gap-1.5 text-sm text-slate-500">
                <Check className="w-3.5 h-3.5 text-blue-600" />
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features + Benefits */}
      <Section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Features */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                What's Included
              </h2>
              <div className="space-y-4">
                {service.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <span className="text-slate-700 leading-relaxed">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">
                Key Benefits
              </h2>
              <div className="space-y-5">
                {service.benefits.map((benefit, i) => (
                  <Card key={i} className="border-slate-100">
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-slate-900 mb-1.5">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed">
                        {benefit.desc}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-slate-900 relative grain-overlay">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white tracking-tight mb-4">
              Ready to Get Started with {service.title}?
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Contact us today for a free consultation. We'll assess your needs
              and provide a tailored solution.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white btn-primary h-12 px-8"
              >
                <Link to="/contact">
                  Request a Quote
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="h-12 px-8 border-slate-600 text-white hover:bg-slate-800 hover:text-white"
              >
                <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  {companyInfo.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <Section className="py-16 lg:py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">
            Related Services
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedServices.map((s) => {
              const SIcon = iconMap[s.icon] || Monitor;
              return (
                <Link key={s.id} to={`/services/${s.id}`}>
                  <Card className="service-card h-full border-slate-200/60 bg-white group cursor-pointer hover:border-blue-100">
                    <CardContent className="p-6">
                      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors duration-300">
                        <SIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-1.5">
                        {s.title}
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed">
                        {s.shortDesc}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
          <div className="mt-8 text-center">
            <Button
              asChild
              variant="outline"
              className="border-slate-300 text-slate-700"
            >
              <Link to="/services">
                <ArrowLeft className="w-4 h-4 mr-1" />
                View All Services
              </Link>
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
}
